import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const LOCATION_INTERVAL = 30000; // 30 seconds

export function LocationTracker() {
  const { user } = useAuth();
  const intervalRef = useRef(null);
  const lastSentRef = useRef({ lat: 0, lng: 0, ts: 0 });

  const sendLocationHttp = useCallback(async (lat, lng) => {
    if (!user?.id) return;
    // Skip if same location sent within last 10s
    const now = Date.now();
    const last = lastSentRef.current;
    if (Math.abs(lat - last.lat) < 0.0001 && Math.abs(lng - last.lng) < 0.0001 && now - last.ts < 10000) return;

    try {
      await api.put(`/users/${user.id}/location`, { lat, lng });
      lastSentRef.current = { lat, lng, ts: now };
    } catch { /* silent */ }
  }, [user?.id]);

  const getAndSendLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => sendLocationHttp(pos.coords.latitude, pos.coords.longitude),
      () => {}, // silent on error
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
    );
  }, [sendLocationHttp]);

  useEffect(() => {
    if (!user) return;

    // Send immediately on mount (login)
    getAndSendLocation();

    // Then poll every 30s
    intervalRef.current = setInterval(getAndSendLocation, LOCATION_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, getAndSendLocation]);

  return null;
}
